import React, { useState, useEffect } from "react";
import { StandardPageWrapper } from "../../../../components/layout/StandardPageWrapper";
import { Routes } from "../../../../root/routes";
import { usePrimaryOrganizationId, useTabs } from "../../../../hooks";
import { useHistory } from "react-router";
import { ExternalArticleCard } from "../../../../components/organisms/cards/ExternalArticleCard";
import { SingleRecordCard } from "../../../../components/organisms/cards/SingleRecordCard";
import { Breadcrumbs, Button, SimpleCheckboxControlled } from "@truevault/aquilo";
import { useRoPASummary } from "../../../../../common/hooks/useRoPA";
import { Fmt } from "../../../../formatters";
import { usePrivacyCenter } from "../../../../hooks/usePrivacyCenter";
import { Api } from "../../../../../common/service/Api";
import clsx from "clsx";

export type RopaPageTabsProps = {
  currentTab: "current" | "history";
};

export const RoPAPageTabs = ({ currentTab }: RopaPageTabsProps) => {
  const routerHistory = useHistory();

  const tabOptions = [
    { label: "Current", value: "current" },
    { label: "Version History", value: "history" },
  ];

  const [_tab, tabs] = useTabs(tabOptions, currentTab, null, (newVal) => {
    let route = "";
    switch (newVal) {
      case "history":
        route = Routes.documents.recordOfProcessingActivities.history;
        break;
      case "current":
        route = Routes.documents.recordOfProcessingActivities.home;
        break;
    }
    if (route) {
      routerHistory.push(route);
    }
  });

  return <div className="mb-lg">{tabs}</div>;
};

export const RoPAPageHome = () => {
  const routerHistory = useHistory();
  const orgId = usePrimaryOrganizationId();

  const [ropaSummary, ropaSummaryRequest] = useRoPASummary(orgId);
  const [privacyCenter, privacyCenterRequest, refreshPrivacyCenter] = usePrivacyCenter(orgId);

  const [ropaDoesNotApply, setRopaDoesNotApply] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (privacyCenter?.ropaDoesNotApply !== undefined) {
      setRopaDoesNotApply(privacyCenter.ropaDoesNotApply);
    }
  }, [privacyCenter?.ropaDoesNotApply]);

  const handleRopaDoesNotApplyChange = async (newChecked: boolean) => {
    setIsSaving(true);
    try {
      await Api.privacyCenter.updatePrivacyCenter(orgId, privacyCenter?.id, {
        ropaDoesNotApply: newChecked,
      });
      setRopaDoesNotApply(newChecked);
      refreshPrivacyCenter();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <StandardPageWrapper requests={[ropaSummaryRequest, privacyCenterRequest]}>
      <div className="tw-boundary">
        <Breadcrumbs
          title="Record of Processing Activities (RoPA)"
          source="Privacy Docs"
          sourceRoute={Routes.documents.root}
        ></Breadcrumbs>
      </div>
      <RoPAPageTabs currentTab="current"></RoPAPageTabs>

      {!ropaDoesNotApply && (
        <div className="flex-row" style={{ gap: "16px" }}>
          <SingleRecordCard
            title="Record of Processing Activities"
            description={
              ropaSummary?.lastUpdated ? (
                <small>Last updated: {Fmt.Date.fmtDate(ropaSummary.lastUpdated)}</small>
              ) : (
                "New"
              )
            }
            chipText={ropaSummary?.isMissingInfo && "Missing Info"}
            chipColor="warning"
            buttons={
              <div className="flex-row mt-md">
                <Button
                  color="primary"
                  disabled={ropaSummaryRequest?.success !== true}
                  onClick={() => {
                    routerHistory.push(Routes.documents.recordOfProcessingActivities.edit);
                  }}
                >
                  View Record
                </Button>
              </div>
            }
            cardStyle={{
              width:
                "448px" /* The External Link card below this ("what is RoPA") is this width - visually match it. */,
            }}
          />
        </div>
      )}

      <h4>Resources and Tips</h4>
      <ExternalArticleCard
        title={"What is a Record of Processing Activities (RoPA)?"}
        description={
          'A "record of processing activities" is a written document that details an organization\'s procedures for collecting, storing, and using personal data.'
        }
        buttonText={"Read More"}
        buttonOnClick={() => {
          window.open(
            "https://knowledge.truevault.com/article/345-records-of-processing-activities",
            "_blank",
          );
        }}
      />

      <div className={clsx("mt-lg", isSaving && "opacity-50 cursor-not-allowed")}>
        <div className="tw-boundary">
          <SimpleCheckboxControlled
            name="ropa-does-not-apply"
            label="I have read the requirements and article about Record of Processing Activities (RoPA) and this requirement does not apply to my organization."
            checked={ropaDoesNotApply}
            onChange={handleRopaDoesNotApplyChange}
            disabled={isSaving}
          />
        </div>
      </div>
    </StandardPageWrapper>
  );
};
